import { cn } from "@/common/utils/cn"
import { VariantProps } from "class-variance-authority"
import { buttonVariants } from "./styles"
import { forwardRef, useId } from "react"
import Spinner from "../Spinner";
import useUserPermissions from "../../hooks/useUserPermissions";
import { Tooltip, type PlacesType } from "react-tooltip";
import Skeleton from "../Skeleton";

export interface ButtonProps extends React.HTMLAttributes<HTMLButtonElement>, VariantProps<typeof buttonVariants> {
  disabled?: boolean
  name?: string
  type?: "button" | "submit" | "reset"
  isLoading?: boolean
  spinnerClassName?: string
  spinnerWrapperClassName?: string;
  tooltipPlace?: PlacesType;
  tooltipMessage?: string;
  noPermissionText?: string
  operationKlassToCheckPermission?: string
  propertiesToCheckPermission?: string[];
  noPermissionBehavior?: 'disable' | 'hide';
  dataTestId?: string;
  disabledClassName?: string;
}

const Button = forwardRef<HTMLButtonElement, ButtonProps>(({
  children,
  variant,
  name,
  className,
  disabled = false,
  isLoading = false,
  type = 'button',
  spinnerClassName,
  spinnerWrapperClassName,
  tooltipPlace = "left",
  tooltipMessage,
  noPermissionText = 'Você não tem permissão',
  operationKlassToCheckPermission,
  propertiesToCheckPermission,
  noPermissionBehavior = 'disable',
  dataTestId,
  disabledClassName,
  style,
  ...props
}, ref) => {
  const { hasUserPermission, isUserPermissionsPending } = useUserPermissions();
  const tooltipId = useId();

  const isPermissionNeeded = !!operationKlassToCheckPermission;
  const hasPermission = isPermissionNeeded && hasUserPermission(operationKlassToCheckPermission, propertiesToCheckPermission);

  if (isPermissionNeeded && noPermissionBehavior === 'hide') {
    if (isUserPermissionsPending) {
      return (
        <Skeleton
          className={cn(
            'py-2 px-4 rounded-sm',
            className,
            'bg-white-100',
          )}
        />
      )
    }

    if (!hasPermission) {
      return null;
    }
  }

  const shouldDisableByPermission = isPermissionNeeded && !hasPermission;
  const shouldShowPermissionTooltip = shouldDisableByPermission && !isUserPermissionsPending;
  const hasCustomTooltip = !!tooltipMessage;
  const shouldShowTooltip = shouldShowPermissionTooltip || hasCustomTooltip;
  const isButtonDisabled = disabled || shouldDisableByPermission;
  const shouldDisablePointerEvents = shouldShowTooltip && isButtonDisabled;
  const hasFullWidthClass = !!className?.includes('w-full');

  const buttonElement = (
    <button
      className={cn(
        buttonVariants({ variant }),
        className,
        (isButtonDisabled || isLoading)
          ? (disabledClassName || 'opacity-50 cursor-default')
          : ''
      )}
      data-testid={dataTestId || (name + '_button')}
      data-tooltip-id={!shouldDisablePointerEvents && shouldShowTooltip ? tooltipId : ''}
      type={type}
      ref={ref}
      {...props}
      style={{
        ...(style || {}),
        pointerEvents: shouldDisablePointerEvents ? 'none' : style?.pointerEvents,
      }}
      disabled={isButtonDisabled}
    >
      {children}

      {(isLoading || (isPermissionNeeded && isUserPermissionsPending)) && (
        <div className={spinnerWrapperClassName}>
          <Spinner className={cn("w-4 h-4 fill-white-800", spinnerClassName)} />
        </div>
      )}
    </button>
  );

  return (
    <>
      {shouldDisablePointerEvents ? (
        <span
          className={cn(hasFullWidthClass ? 'block w-full' : 'inline-flex')}
          data-tooltip-id={shouldShowTooltip ? tooltipId : ''}
        >
          {buttonElement}
        </span>
      ) : (
        buttonElement
      )}

      {shouldShowTooltip && (
        <Tooltip id={tooltipId} place={tooltipPlace}>{tooltipMessage || noPermissionText}</Tooltip>
      )}
    </>
  )
});

Button.displayName = "Button"

export default Button
